import React, { useEffect, useState } from 'react';
import { SelectOpt } from '../CommonTypes';
import './DateSelector.scss';
import Tooltip from '@mui/material/Tooltip';
import { getDayMonthOrder } from '../Formatter';
import SelectInput, { SelectInputHelper } from '../SelectInput/SelectInput';
import { t } from 'i18next';

export type DateSelectorProps = {
  label?: string;
  value?: Date;
  minDate: Date;
  maxDate: Date;
  invalidDateMessage?: string;
  updateDobError?: boolean;
  validateDateRange?: boolean;
  tooltipTitle?: string;
  tooltipText?: string;
  culture: string;
  hideDay?: boolean;
  disabled?: boolean;
  name?: string;
  onChange: (value: Date | undefined) => void;
  onValidated: (isValid: boolean) => void;
};

const DateSelector = ({
  label,
  value,
  minDate,
  maxDate,
  invalidDateMessage,
  updateDobError,
  validateDateRange,
  tooltipTitle,
  tooltipText,
  culture,
  hideDay,
  disabled,
  name = '',
  onChange,
  onValidated
}: DateSelectorProps): JSX.Element => {
  const defaultDay = hideDay ? '01' : SelectInputHelper.NullValue();
  const [day, setDay] = useState(defaultDay);
  const [month, setMonth] = useState(SelectInputHelper.NullValue());
  const [year, setYear] = useState(SelectInputHelper.NullValue());
  const [error, setError] = useState('');
  const dayBeforeMonth = getDayMonthOrder(culture);
  const padValue = (text: string): string => {
    if (text.length === 1) {
      text = `0${text}`;
    }

    return text;
  };

  useEffect(() => {
    if (value) {
      value = typeof value === 'string' ? new Date(value) : value;
      setDay(padValue(value.getDate().toString()));
      setMonth(padValue((value.getMonth() + 1).toString()));
      setYear(value.getFullYear().toString());
    }
  }, [value]);

  useEffect(() => {
    if (disabled) {
      setDay(defaultDay);
      setMonth(SelectInputHelper.NullValue());
      setYear(SelectInputHelper.NullValue());
    }
  }, [disabled]);

  useEffect(() => {
    setError(invalidDateMessage || '');
  }, [updateDobError, invalidDateMessage]);

  useEffect(() => {
    validate(day, month, year);
  }, [day, month, year]);

  const getLabelPanel = () => {
    if (label && label.length > 0) {
      return <div className='d-block'>{label}</div>;
    }
  };

  const validate = (d: string, m: string, y: string) => {
    if (
      d === defaultDay &&
      m === SelectInputHelper.NullValue() &&
      y === SelectInputHelper.NullValue()
    ) {
      setError('');
      onChange(undefined);
      onValidated(true);
      return;
    }

    let isValid = true;
    const selectedDate = new Date(
      Date.UTC(Number(year), Number(month) - 1, Number(day))
    );
    const errorMessage = t('summary.invalid-date');

    if (selectedDate.getMonth() === Number(month) - 1) {
      if (
        minDate &&
        validateDateRange === true &&
        selectedDate.setHours(0, 0, 0, 0) < minDate.setHours(0, 0, 0, 0)
      ) {
        isValid = false;
      }

      if (
        maxDate &&
        validateDateRange === true &&
        selectedDate.setHours(0, 0, 0, 0) > maxDate.setHours(0, 0, 0, 0)
      ) {
        isValid = false;
      }
    } else {
      isValid = false;
    }

    if (isValid) {
      setError('');
      onChange(selectedDate);
    } else {
      setError(errorMessage);
    }

    onValidated(isValid);
  };

  const onChangeValue = (val: string, controlName: string) => {
    switch (controlName) {
      case 'day-select':
        setDay(val);
        break;
      case 'month-select':
        setMonth(val);
        break;
      case 'year-select':
        setYear(val);
        break;
    }
  };

  const getOptions = (
    startOption: number,
    endOption: number,
    reverse: boolean = false
  ): SelectOpt[] => {
    const options: SelectOpt[] = Array(endOption - startOption + 1)
      .fill(undefined)
      .map((_, i) => {
        const val: string = padValue(
          reverse ? (endOption - i).toString() : (i + startOption).toString()
        );

        const opt: SelectOpt = {
          id: val,
          text: val,
          value: val,
          disabled: false
        };

        return opt;
      });

    return options;
  };

  const dayOptions: SelectOpt[] = getOptions(1, 31);
  const monthOptions: SelectOpt[] = getOptions(1, 12);
  const yearOptions: SelectOpt[] = getOptions(
    minDate.getFullYear(),
    maxDate.getFullYear(),
    true
  );

  const getInputPanel = () => {
    let inputClasses = 'date-container';
    if (error && error.length > 0) {
      inputClasses = 'date-container has-error';
    }

    const daySelect = hideDay ? (
      <></>
    ) : (
      <SelectInput
        name={`day-select${name}`}
        options={dayOptions}
        addDefaultOption
        defaultText='DD'
        onChange={(selectedValue: string) =>
          onChangeValue(selectedValue, 'day-select')
        }
        value={day}
        readonly={disabled}
      />
    );

    const monthSelect = (
      <SelectInput
        name={`month-select${name}`}
        options={monthOptions}
        addDefaultOption
        defaultText='MM'
        onChange={(selectedValue: string) =>
          onChangeValue(selectedValue, 'month-select')
        }
        value={month}
        readonly={disabled}
      />
    );

    const yearSelect = (
      <SelectInput
        name={`year-select${name}`}
        options={yearOptions}
        addDefaultOption
        defaultText='YYYY'
        onChange={(selectedValue: string) =>
          onChangeValue(selectedValue, 'year-select')
        }
        value={year}
        readonly={disabled}
      />
    );

    return (
      <div className='dob-control'>
        <div className={inputClasses}>
          <div className='me-1 dateselector'>
            {dayBeforeMonth ? daySelect : monthSelect}
          </div>
          <div className='me-1 dateselector'>
            {dayBeforeMonth ? monthSelect : daySelect}
          </div>
          <div className='year-selector dateselector'>{yearSelect}</div>
        </div>
        {tooltipTitle && tooltipText && (
          <Tooltip
            title={tooltipText}
            enterTouchDelay={0}
            arrow
            placement='right'>
            <p data-testid='dob-tooltip'>{tooltipTitle}</p>
          </Tooltip>
        )}
      </div>
    );
  };

  const getErrorPanel = () => {
    if (error && error.length > 0) {
      return (
        <div className='has-error'>
          <div className='input error-margin'>{error}</div>
        </div>
      );
    }
  };

  const labelPanel = getLabelPanel();
  const inputPanel = getInputPanel();
  const errorPanel = getErrorPanel();

  return (
    <>
      {labelPanel}
      {inputPanel}
      {errorPanel}
    </>
  );
};

export default DateSelector;
